/**
 * 정책 트리 + 청구 데이터 결합 유틸.
 *
 * 백엔드(stats-statistic 월 마감 시 저장 또는 web-api 실시간 계산)에서 항목별 fee를
 * detail.itemFees에 담아주므로, 프론트는 thin getter로 단순 조회만 한다.
 *
 * - parseTierDiscountInfo:  detail.tierDiscountInfo JSON 파싱 (구간별 표시용)
 * - computeItemUsage:       detail.itemFees[itemNumber] 조회 (백엔드 응답값)
 * - computeGroupTotal:      정책 트리의 그룹별 사용 합계 (백엔드 groupTotals 우선, fallback 합산)
 * - findGroupCompensation:  그룹별 보전금액 매칭
 */

/**
 * detail.tierDiscountInfo (JSON string 또는 object)에서 applied_tiers 배열을 파싱.
 * 실패 시 빈 배열 반환.
 */
export function parseTierDiscountInfo(raw) {
    if (!raw) return [];
    try {
        if (typeof raw === 'object') {
            return raw.applied_tiers || [];
        }
        const parsed = JSON.parse(raw);
        return parsed?.applied_tiers || [];
    } catch {
        return [];
    }
}

/**
 * 항목번호별 사용 요금 (백엔드 응답값 thin getter).
 *
 * 백엔드 응답의 detail.itemFees는 stringified int 키 ("2", "3", ...).
 * 새 항목 추가 시 코드 변경 불필요 — 백엔드가 응답에 포함하면 자동으로 표시됨.
 */
export function computeItemUsage(itemNumber, detail) {
    if (!detail || itemNumber == null) return 0;
    return detail.itemFees?.[String(itemNumber)] ?? 0;
}

/**
 * 정책 트리의 그룹별 합계 조회 (백엔드 응답값).
 *
 * detail.groupTotals[group.serviceItemNumber] 에서 백엔드가 이미 합산한 값을 읽음.
 * 없으면 과거 응답 호환으로 프론트에서 fallback 합산.
 *
 * @param {Object} group - policyTree.serviceGroupList[i]
 * @param {Object} detail - billingDetailsList[i]
 * @returns {number}
 */
export function computeGroupTotal(group, detail) {
    if (!group || !detail) return 0;
    const fromBackend = detail.groupTotals?.[String(group.serviceItemNumber)];
    if (fromBackend != null) return fromBackend;
    const items = group.servicePriceList || [];
    return items.reduce((sum, item) => {
        return sum + computeItemUsage(item.serviceItemNumber, detail);
    }, 0);
}

/**
 * compensationInfo.groups 배열에서 특정 group(serviceGroup)의 보전금액 정보 조회.
 * group.uuid 또는 group.serviceItemName으로 매칭.
 */
export function findGroupCompensation(group, compensationInfo) {
    if (!group || !compensationInfo?.groups) return null;
    const groups = compensationInfo.groups;
    // 우선 uuid로 매칭 (가장 정확)
    const byUuid = groups.find(g => g.uuid && group.uuid && g.uuid === group.uuid);
    if (byUuid) return byUuid;
    // fallback: 이름으로 매칭
    const groupName = (group.serviceItemName || '').toUpperCase();
    return groups.find(g => {
        const name = (g.name || '').toUpperCase();
        return name === groupName;
    }) || null;
}
